/* GraphPanel — interactive knowledge graph (custom canvas force sim),
   ported 1:1 from the design prototype's graph.jsx. Renders the Graph
   carousel slide: canvas + remote control + selection panel. */
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { markFeatureTried } from "../lib/settings";
import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CornerUpLeft,
  Dot,
  Eye,
  Minus,
  Plus,
  RotateCcw,
  X,
} from "lucide-react";
import { data } from "../lib/data";
import {
  getExperience,
  getIndustry,
  getProject,
  getProjectsForExperience,
  getRole,
  getSkill,
  getTechnology,
} from "../lib/queries";
import { TECH_CAT_HUE } from "../lib/visuals";
import type { Experience, Project } from "../types";

type TagType = "industry" | "technology" | "skill" | "role";
type NodeType = "experience" | "project" | TagType;

interface GNode {
  id: string;
  type: NodeType;
  entityId: string;
  label: string;
}
interface GLink {
  source: string;
  target: string;
}
interface GGraph {
  nodes: GNode[];
  links: GLink[];
}
interface Focus {
  expId: string | null;
  projId: string | null;
}
interface Selected {
  type: NodeType;
  id: string;
}
interface Crumb {
  type: NodeType;
  id: string;
  label: string;
}
interface GState {
  selected: Selected | null;
  viewBy: { type: TagType; id: string } | null;
  trail: Crumb[];
  focus: Focus;
  history: Focus[];
  histIdx: number;
}
interface PosRec {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const GTYPE: Record<NodeType, { r: number; label: string }> = {
  experience: { r: 30, label: "Experience" },
  project: { r: 21, label: "Engagement" },
  industry: { r: 16, label: "Industry" },
  technology: { r: 14, label: "Technology" },
  skill: { r: 13, label: "Skill" },
  role: { r: 13, label: "Role" },
};

/* Fewer bubbles on screen → scale every bubble (and its label) up so the canvas
   stays full and readable. 1× at ≥14 nodes, up to ~2.1× for a handful. */
function nodeScale(count: number): number {
  if (count <= 1) return 2.1;
  if (count >= 14) return 1;
  return 1 + ((14 - count) / 13) * 1.1;
}

const TAG_PARAM: Record<TagType, string> = {
  industry: "industry",
  technology: "tech",
  skill: "skill",
  role: "role",
};

/* Tap-vs-drag slop, in CSS px. A finger never taps perfectly still — its contact point drifts a
   few px between touchdown and lift — so a mouse-sized 3px slop reclassifies every touch tap as a
   drag and swallows the drill-down (the bubbles only respond on desktop). Touch/pen get a more
   forgiving slop (≈ the platform touch-slop); the mouse keeps the tight slop so deliberate small
   drags still register as drags, leaving desktop behavior unchanged. */
const tapSlop = (pointerType: string): number => (pointerType === "mouse" ? 3 : 10);

/* Graph-specific industry palette (brighter than the lens pastels — these sit
   as small filled discs on the canvas). */
const GRAPH_INDUSTRY_HUE: Record<string, string> = {
  healthcare: "#0d9488",
  "financial-services": "#4f46e5",
  insurance: "#7c3aed",
  government: "#64748b",
  utilities: "#059669",
  travel: "#0284c7",
  telecom: "#c026d3",
  manufacturing: "#8a8178",
  technology: "#d97706",
};

interface ThemeLike {
  ink: string;
  accent: string;
}

function nodeColor(type: NodeType, id: string, theme: ThemeLike): string {
  if (type === "experience") return theme.ink;
  if (type === "project") return projColor(id);
  if (type === "industry") return GRAPH_INDUSTRY_HUE[id] || "#0d9488";
  if (type === "technology") {
    const cat = getTechnology(id)?.category;
    return (cat ? TECH_CAT_HUE[cat] : undefined) || "#8b5cf6";
  }
  if (type === "skill") return "#d97706";
  return "#64748b";
}

/* Per-project line color from one cohesive family (shared S/L, hue on the
   golden angle so sibling projects always land far apart). Outline only — no
   filled bubble — so projects read lighter than the company nodes. */
function projColor(id: string): string {
  const i = data.projects.findIndex((p) => p.id === id);
  const hue = ((i < 0 ? 0 : i) * 137.508) % 360;
  return `hsl(${hue.toFixed(1)} 58% 52%)`;
}

function tagNodeId(type: TagType, id: string): string {
  return `${type}:${id}`;
}

/* ── Canvas icon strokes (lucide 24×24 inner markup, drawn as images) ─────*/
const ICON_PATHS: Record<string, string> = {
  dot: '<circle cx="12" cy="12" r="3"/>',
  layers:
    '<path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 17 9 5 9-5"/>',
  lightbulb:
    '<path d="M9 18h6M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.76.76 1.23 1.52 1.41 2.5"/>',
  trophy:
    '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>',
  hospital:
    '<path d="M12 6v4M14 8h-4"/><path d="M18 22V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v18"/><path d="M2 22h20"/><path d="M6 22v-8h12v8"/>',
  "heart-pulse":
    '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/><path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"/>',
  landmark:
    '<path d="M3 22h18"/><path d="M6 18v-7M10 18v-7M14 18v-7M18 18v-7"/><path d="M4 11h16"/><path d="m12 2 8 5H4l8-5Z"/>',
  shield:
    '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>',
  building:
    '<rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01"/>',
  zap: '<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z"/>',
  plane:
    '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2Z"/>',
  antenna:
    '<path d="M2 12 7 2M5 12 9.5 2.5M2 12h20M22 12 17 2M19 12 14.5 2.5M12 12v10"/>',
  cog: '<path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"/><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M20 12h2M2 12h2"/>',
  cpu: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2"/>',
  headset:
    '<path d="M3 14h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4a9 9 0 0 1 18 0v4a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h2"/><path d="M19 19a4 4 0 0 1-4 3h-3"/>',
  sparkles:
    '<path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.14-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0L14.06 8.5A2 2 0 0 0 15.5 9.94l6.14 1.58a.5.5 0 0 1 0 .96L15.5 14.06a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z"/><path d="M20 3v4M22 5h-4M4 17v2M5 18H3"/>',
  briefcase:
    '<rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  megaphone:
    '<path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>',
  cloud:
    '<path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>',
  database:
    '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/>',
  "git-branch":
    '<line x1="6" x2="6" y1="3" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/>',
  "message-square":
    '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  code: '<path d="m16 18 6-6-6-6"/><path d="m8 6-6 6 6 6"/>',
};

const INDUSTRY_ICON: Record<string, string> = {
  healthcare: "heart-pulse",
  "financial-services": "landmark",
  insurance: "shield",
  government: "building",
  utilities: "zap",
  travel: "plane",
  telecom: "antenna",
  manufacturing: "cog",
  technology: "cpu",
};
const TECH_CAT_ICON: Record<string, string> = {
  ccaas: "headset",
  ai: "sparkles",
  crm: "briefcase",
  marketing: "megaphone",
  itsm: "cog",
  support: "headset",
  telecom: "antenna",
  middleware: "layers",
  eam: "cog",
  infrastructure: "cloud",
  data: "database",
  integration: "git-branch",
  collaboration: "message-square",
  language: "code",
};

/* ── Company-logo image sources (single registry for the canvas) ──────────*/
const FAVICON = (domain: string, size = 128) =>
  `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${domain}&size=${size}`;

const svgDataUri = (svg: string) =>
  "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
const wrap100 = (inner: string) =>
  `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`;

/* Cisco — signature vertical "bridge" bars */
const ciscoMark = (() => {
  const heights = [22, 38, 22, 38, 22, 38, 22, 38, 22];
  const bw = 5;
  const gap = 4.6;
  const total = heights.length * bw + (heights.length - 1) * gap;
  let x = (100 - total) / 2;
  let bars = "";
  for (const h of heights) {
    const y = 50 - h / 2;
    bars += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw}" height="${h}" rx="2.4" fill="#1BA0D7"/>`;
    x += bw + gap;
  }
  return wrap100(bars);
})();

/* Deloitte — black "D" with the signature green dot */
const deloitteMark = wrap100(
  `<text x="42" y="50" dy="0.34em" text-anchor="middle" font-family="Hanken Grotesk, Arial, sans-serif" font-size="62" font-weight="700" fill="#000">D</text>` +
    `<circle cx="74" cy="63" r="8" fill="#86BC25"/>`,
);

/* Cybernet — no surviving logo; stylized "CSS" initials (same as other views) */
const cybernetMark = wrap100(
  `<text x="50" y="50" dy="0.34em" text-anchor="middle" font-family="JetBrains Mono, monospace" font-size="30" font-weight="800" letter-spacing="1" fill="#4a463d">CSS</text>`,
);

const IBM_SRC =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/IBM_logo.svg/1920px-IBM_logo.svg.png";

const EXP_LOGO_SRC: Record<string, string> = {
  deloitte: svgDataUri(deloitteMark),
  cisco: svgDataUri(ciscoMark),
  metlife: "/logos/metlife-mark.png",
  "bcbs-nc": "https://icons.duckduckgo.com/ip3/bluecrossnc.com.ico",
  "bofa-merrill": "/logos/bofa-flagscape.png",
  "verizon-business":
    "https://upload.wikimedia.org/wikipedia/commons/5/5a/Verizon_2024_%28symbol%29.svg",
  "ibm-gs": IBM_SRC,
  "ibm-sg": IBM_SRC,
  "ibm-pcd": IBM_SRC,
  "ibm-tvm": IBM_SRC,
  startec: "/logos/startec.png",
  cybernet: svgDataUri(cybernetMark),
};

/* Initials fallback when an image source is missing or fails to load */
const MONO: Record<string, { letters: string; color: string }> = {
  deloitte: { letters: "D.", color: "#0a8f5b" },
  cisco: { letters: "Ci", color: "#1BA0D7" },
  "sj-enterprise": { letters: "SJ", color: "#475569" },
  metlife: { letters: "M", color: "#4f46e5" },
  "bcbs-nc": { letters: "BC", color: "#0284c7" },
  "bofa-merrill": { letters: "BA", color: "#1f4ba6" },
  "ibm-gs": { letters: "IBM", color: "#4f46e5" },
  "ibm-sg": { letters: "IBM", color: "#4f46e5" },
  "ibm-pcd": { letters: "IBM", color: "#4f46e5" },
  "ibm-tvm": { letters: "IBM", color: "#4f46e5" },
  "verizon-business": { letters: "Vz", color: "#CD040B" },
  startec: { letters: "St", color: "#7c7468" },
  cybernet: { letters: "Cy", color: "#7c7468" },
};

/* Authored brand SVGs for individual technologies (rendered ahead of favicons).
   Amazon Connect Customer 3-square mark — author-supplied; do not substitute. */
const TECH_LOGOS: Record<string, string> = {
  "amazon-connect": `<svg viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M484.311 292.676C484.311 311.287 484.311 320.593 480.689 327.701C477.503 333.954 472.419 339.038 466.167 342.224C459.058 345.846 449.753 345.846 431.142 345.846H351.449C343.321 345.846 339.257 345.846 335.432 346.764C332.042 347.578 328.8 348.921 325.827 350.743C322.473 352.798 319.6 355.672 313.852 361.419L306.033 369.239C300.286 374.986 297.412 377.86 295.357 381.213C293.535 384.186 292.193 387.428 291.379 390.818C290.46 394.643 290.46 398.707 290.46 406.835V431.138C290.46 449.749 290.46 459.055 286.839 466.163C283.653 472.416 278.569 477.5 272.316 480.686C265.208 484.308 255.902 484.308 237.291 484.308H205.171C186.56 484.308 177.254 484.308 170.146 480.686C163.893 477.5 158.809 472.416 155.623 466.163C152.001 459.055 152.001 449.749 152.001 431.138V399.015C152.001 380.405 152.001 371.099 155.623 363.991C158.809 357.738 163.893 352.654 170.146 349.468C177.254 345.846 186.559 345.846 205.17 345.846L229.078 345.846C237.206 345.846 241.27 345.846 245.094 344.928C248.485 344.114 251.726 342.771 254.699 340.949C258.053 338.894 260.927 336.02 266.674 330.273L274.896 322.05C280.643 316.303 283.517 313.429 285.572 310.076C287.393 307.103 288.736 303.861 289.55 300.471C290.468 296.646 290.468 292.582 290.468 284.455V205.169C290.468 186.558 290.468 177.253 294.09 170.144C297.276 163.892 302.36 158.808 308.613 155.622C315.721 152 325.027 152 343.637 152H431.142C449.753 152 459.058 152 466.167 155.622C472.419 158.808 477.503 163.892 480.689 170.144C484.311 177.253 484.311 186.558 484.311 205.169V292.676Z" fill="#E860FF"></path><path d="M871.988 389.6C871.988 408.211 871.988 417.516 868.366 424.625C865.18 430.878 860.096 435.961 853.844 439.147C846.735 442.769 837.43 442.769 818.819 442.769H642.411C634.283 442.769 630.219 442.769 626.394 443.687C623.003 444.501 619.762 445.844 616.789 447.666C613.435 449.721 610.561 452.595 604.814 458.342L596.796 466.36C591.049 472.108 588.175 474.981 586.12 478.335C584.298 481.308 582.956 484.55 582.142 487.94C581.224 491.765 581.224 495.829 581.224 503.956V624.985C581.224 643.596 581.224 652.901 577.602 660.01C574.416 666.262 569.332 671.346 563.079 674.532C555.971 678.154 546.665 678.154 528.054 678.154H407.03C398.902 678.154 394.838 678.154 391.013 679.072C387.623 679.886 384.381 681.229 381.408 683.051C378.054 685.106 375.181 687.98 369.433 693.727L361.415 701.745C355.668 707.492 352.795 710.366 350.74 713.72C348.918 716.693 347.575 719.934 346.761 723.325C345.843 727.149 345.843 731.213 345.843 739.341V818.831C345.843 837.442 345.843 846.747 342.221 853.856C339.035 860.108 333.951 865.192 327.699 868.378C320.59 872 311.285 872 292.674 872H205.169C186.558 872 177.253 872 170.144 868.378C163.892 865.192 158.808 860.108 155.622 853.856C152 846.747 152 837.442 152 818.831V731.323C152 712.712 152 703.407 155.622 696.298C158.808 690.045 163.892 684.962 170.144 681.776C177.253 678.154 186.558 678.154 205.169 678.154H284.656C292.784 678.154 296.848 678.154 300.672 677.236C304.063 676.422 307.305 675.079 310.278 673.257C313.631 671.202 316.505 668.328 322.252 662.581L330.27 654.563C336.017 648.815 338.891 645.942 340.946 642.588C342.768 639.615 344.111 636.374 344.925 632.983C345.843 629.158 345.843 625.094 345.843 616.967V495.938C345.843 477.327 345.843 468.022 349.465 460.914C352.651 454.661 357.734 449.577 363.987 446.391C371.096 442.769 380.401 442.769 399.012 442.769H520.036C528.164 442.769 532.228 442.769 536.053 441.851C539.444 441.037 542.685 439.694 545.658 437.872C549.012 435.817 551.886 432.943 557.633 427.196L565.651 419.178C571.398 413.431 574.272 410.557 576.327 407.204C578.149 404.23 579.491 400.989 580.305 397.598C581.224 393.774 581.224 389.71 581.224 381.582V205.169C581.224 186.558 581.224 177.253 584.845 170.144C588.031 163.892 593.115 158.808 599.368 155.622C606.476 152 615.782 152 634.393 152H818.819C837.43 152 846.735 152 853.844 155.622C860.096 158.808 865.18 163.892 868.366 170.144C871.988 177.253 871.988 186.558 871.988 205.169V389.6Z" fill="#FF6200"></path><path d="M872 680.368C872 698.979 872 708.284 868.378 715.393C865.192 721.646 860.109 726.729 853.856 729.915C846.747 733.537 837.442 733.537 818.831 733.537H739.139C731.011 733.537 726.947 733.537 723.122 734.455C719.731 735.269 716.49 736.612 713.517 738.434C710.163 740.489 707.289 743.363 701.542 749.11L693.73 756.923C687.983 762.67 685.109 765.544 683.054 768.897C681.232 771.87 679.889 775.112 679.075 778.503C678.157 782.327 678.157 786.391 678.157 794.519V818.829C678.157 837.44 678.157 846.746 674.535 853.854C671.349 860.107 666.266 865.191 660.013 868.377C652.904 871.999 643.599 871.999 624.988 871.999H592.867C574.256 871.999 564.951 871.999 557.842 868.377C551.59 865.191 546.506 860.107 543.32 853.854C539.698 846.746 539.698 837.44 539.698 818.829V786.706C539.698 768.095 539.698 758.79 543.32 751.681C546.506 745.429 551.59 740.345 557.842 737.159C564.951 733.537 574.256 733.537 592.867 733.537H616.765C624.893 733.537 628.957 733.537 632.781 732.619C636.172 731.805 639.413 730.462 642.387 728.64C645.74 726.585 648.614 723.711 654.361 717.964L662.585 709.74C668.332 703.993 671.205 701.119 673.26 697.766C675.082 694.793 676.425 691.551 677.239 688.161C678.157 684.336 678.157 680.272 678.157 672.144V592.86C678.157 574.249 678.157 564.944 681.779 557.835C684.965 551.582 690.049 546.499 696.302 543.313C703.41 539.691 712.715 539.691 731.326 539.691H818.831C837.442 539.691 846.747 539.691 853.856 543.313C860.109 546.499 865.192 551.582 868.378 557.835C872 564.944 872 574.249 872 592.86V680.368Z" fill="#00ABBA"></path></svg>`,
};

/* Brand favicons for individual technologies (everything else → category icon) */
const TECH_DOMAIN: Record<string, string> = {
  "amazon-connect": "aws.amazon.com",
  "amazon-q-connect": "aws.amazon.com",
  "aws-bedrock": "aws.amazon.com",
  "amazon-lex": "aws.amazon.com",
  "bedrock-agentcore": "aws.amazon.com",
  "claude-code": "claude.ai",
  "claude-design": "claude.ai",
  "claude-cowork": "claude.ai",
  chatgpt: "openai.com",
  gemini: "google.com",
  salesforce: "salesforce.com",
  "salesforce-marketing-cloud": "salesforce.com",
  servicenow: "servicenow.com",
  zoho: "zoho.com",
  zendesk: "zendesk.com",
  "amdocs-connectx": "amdocs.com",
  websphere: "ibm.com",
  "websphere-portal": "ibm.com",
  "ibm-maximo": "ibm.com",
  "aix-vio": "ibm.com",
  "lotus-ei": "ibm.com",
  "lotus-notes": "ibm.com",
  "sap-hana": "sap.com",
  "sap-rfc": "sap.com",
  "oracle-db": "oracle.com",
  java: "java.com",
  "adobe-pdf-sdk": "adobe.com",
  checkr: "checkr.com",
  nodejs: "nodejs.org",
  react: "react.dev",
  python: "python.org",
};

/* ── Image caches for canvas drawing ──────────────────────────────────────*/
interface ImgRec {
  img: HTMLImageElement | null;
  ok: boolean;
}

const LOGO_IMG = new Map<string, ImgRec>(); // expId -> rec
function getLogoImg(eid: string): ImgRec {
  const cached = LOGO_IMG.get(eid);
  if (cached) return cached;
  const rec: ImgRec = { img: null, ok: false };
  LOGO_IMG.set(eid, rec);
  const src = EXP_LOGO_SRC[eid];
  if (src) {
    const img = new Image();
    img.onload = () => {
      rec.ok = true;
    };
    img.onerror = () => {
      rec.ok = false;
    };
    img.src = src;
    rec.img = img;
  }
  return rec;
}

const IMG_CACHE = new Map<string, ImgRec>(); // src -> rec
function getImg(src: string | null): ImgRec | null {
  if (!src) return null;
  const cached = IMG_CACHE.get(src);
  if (cached) return cached;
  const rec: ImgRec = { img: new Image(), ok: false };
  rec.img!.onload = () => {
    rec.ok = true;
  };
  rec.img!.onerror = () => {
    rec.ok = false;
  };
  rec.img!.src = src;
  IMG_CACHE.set(src, rec);
  return rec;
}

function iconImg(name: string, color: string): ImgRec | null {
  const inner = ICON_PATHS[name] || ICON_PATHS.dot;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
  return getImg("data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg));
}

function techLogoSrc(id: string): string | null {
  const authored = TECH_LOGOS[id];
  if (authored) return svgDataUri(authored);
  const domain = TECH_DOMAIN[id];
  if (domain) return FAVICON(domain, 64);
  return null;
}

/* draw an image (logo/icon) contained within a circle of radius r at (x,y) */
function drawMarkInDisc(
  ctx: CanvasRenderingContext2D,
  rec: ImgRec | null,
  x: number,
  y: number,
  r: number,
  fill: string,
): boolean {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, 7);
  ctx.fillStyle = fill;
  ctx.fill();
  if (rec && rec.ok && rec.img) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, r - 2, 0, 7);
    ctx.clip();
    const box = (r - 3) * 2 * 0.84;
    const iw = rec.img.naturalWidth || rec.img.width || 1;
    const ih = rec.img.naturalHeight || rec.img.height || 1;
    const s = Math.min(box / iw, box / ih);
    ctx.drawImage(rec.img, x - (iw * s) / 2, y - (ih * s) / 2, iw * s, ih * s);
    ctx.restore();
    return true;
  }
  return false;
}

function abbrevCo(s: string): string {
  return s
    .replace(/\bConsulting\b/, "")
    .replace(/\bSystems\b/, "")
    .replace(/\bSolutions,?\s*Inc\.?\b/, "")
    .replace(/\bGlobal Communications\b/, "")
    .replace(/\bSoftware Systems\b/, "")
    .replace(/\bMerrill Lynch\b/, "Merrill")
    .replace(/Blue Cross Blue Shield of North Carolina/, "BCBS NC")
    .replace(/\(.*\)/, "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ── Tag-field accessors (typed equivalent of the prototype's e[field]) ───*/
function expTagIds(e: Experience, t: TagType): string[] {
  if (t === "industry") return e.industries;
  if (t === "technology") return e.technologies;
  if (t === "skill") return e.skills;
  return e.roleIds ?? [];
}
function projTagIds(p: Project, t: TagType): string[] {
  if (t === "industry") return p.industries;
  if (t === "technology") return p.technologies;
  if (t === "skill") return p.skills;
  return []; // role tags are not on projects
}

function tagNode(type: TagType, id: string): GNode {
  const o =
    type === "industry"
      ? getIndustry(id)
      : type === "technology"
        ? getTechnology(id)
        : type === "skill"
          ? getSkill(id)
          : getRole(id);
  return { id: tagNodeId(type, id), type, entityId: id, label: o?.label || id };
}
function expNode(e: Experience): GNode {
  return {
    id: "exp:" + e.id,
    type: "experience",
    entityId: e.id,
    label: abbrevCo(e.company),
  };
}
function projNode(p: Project): GNode {
  return { id: "proj:" + p.id, type: "project", entityId: p.id, label: p.title };
}

function buildGraph(state: GState): GGraph {
  const nodes = new Map<string, GNode>();
  const links: GLink[] = [];
  const add = (n: GNode) => {
    if (!nodes.has(n.id)) nodes.set(n.id, n);
  };

  if (state.viewBy) {
    const { type, id } = state.viewBy;
    add(tagNode(type, id));
    const expIds = new Set<string>();
    for (const e of data.experiences)
      if (expTagIds(e, type).includes(id)) expIds.add(e.id);
    if (type !== "role")
      for (const p of data.projects)
        if (projTagIds(p, type).includes(id)) expIds.add(p.experienceId);
    for (const eid of expIds) {
      const e = getExperience(eid);
      if (!e) continue;
      const en = expNode(e);
      add(en);
      if (expTagIds(e, type).includes(id))
        links.push({ source: tagNodeId(type, id), target: en.id });
      if (type !== "role") {
        for (const p of getProjectsForExperience(eid))
          if (projTagIds(p, type).includes(id)) {
            const pn = projNode(p);
            add(pn);
            links.push({ source: en.id, target: pn.id });
            links.push({ source: tagNodeId(type, id), target: pn.id });
          }
      }
    }
    return { nodes: [...nodes.values()], links };
  }

  /* Focused drill-down: clicking a node hides its peers and shows just its
     parent chain + children, so the selection owns the whole canvas. */
  const f = state.focus;
  if (f.projId) {
    const p = getProject(f.projId);
    if (p) {
      const e = getExperience(p.experienceId);
      if (e) {
        add(expNode(e));
        links.push({ source: "exp:" + e.id, target: "proj:" + p.id });
      }
      add(projNode(p));
      for (const id of p.industries) {
        add(tagNode("industry", id));
        links.push({ source: "proj:" + p.id, target: tagNodeId("industry", id) });
      }
      for (const id of p.technologies) {
        add(tagNode("technology", id));
        links.push({ source: "proj:" + p.id, target: tagNodeId("technology", id) });
      }
      for (const id of p.skills) {
        add(tagNode("skill", id));
        links.push({ source: "proj:" + p.id, target: tagNodeId("skill", id) });
      }
      return { nodes: [...nodes.values()], links };
    }
  }
  if (f.expId) {
    const e = getExperience(f.expId);
    if (e) {
      add(expNode(e));
      for (const p of getProjectsForExperience(f.expId)) {
        add(projNode(p));
        links.push({ source: "exp:" + e.id, target: "proj:" + p.id });
      }
      return { nodes: [...nodes.values()], links };
    }
  }
  for (const e of data.experiences) add(expNode(e));
  return { nodes: [...nodes.values()], links };
}

/* Build a breadcrumb chain from a clicked node up to its root via current links. */
function trailFor(node: GNode, graph: GGraph): Crumb[] {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const parent = new Map<string, string>();
  for (const l of graph.links) {
    if (!parent.has(l.target)) parent.set(l.target, l.source);
  }
  const chain: GNode[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = node.id;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    const n = byId.get(cur);
    if (n) chain.push(n);
    cur = parent.get(cur);
  }
  chain.reverse();
  return chain.map((n) => ({ type: n.type, id: n.entityId, label: n.label }));
}

/* per-frame label registry — labels slide down a slot until they stop overlapping */
const __labelRects: { x: number; y: number; w: number; h: number }[] = [];
function resetLabelRects() {
  __labelRects.length = 0;
}
function labelBelow(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lh: number,
  maxLines: number,
) {
  const words = String(text).split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines) break;
    } else line = test;
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(last + "…").width > maxW && last.length)
      last = last.slice(0, -1);
    if (lines.join(" ").length < String(text).length)
      lines[maxLines - 1] = last + "…";
  }
  const lw = Math.min(
    maxW,
    Math.max(1, ...lines.map((l) => ctx.measureText(l).width)),
  );
  const lhTot = lines.length * lh;
  let ly = y;
  const hits = (a: { x: number; y: number; w: number; h: number }) =>
    __labelRects.some(
      (r) => a.x < r.x + r.w && a.x + a.w > r.x && a.y < r.y + r.h && a.y + a.h > r.y,
    );
  for (let tries = 0; tries < 4; tries++) {
    const rect = { x: x - lw / 2, y: ly, w: lw, h: lhTot };
    if (!hits(rect) || tries === 3) {
      __labelRects.push(rect);
      break;
    }
    ly += lh; // collision → slide down one line-slot and retry
  }
  lines.forEach((l, i) => ctx.fillText(l, x, ly + i * lh));
}

const LEGEND_THEME: ThemeLike = { ink: "var(--ink)", accent: "var(--accent)" };

// A FRESH root state on each call. Used both as the initial state and by reset(): a new object
// reference every time, so setState always re-renders and never hits React's same-reference bail-out
// (which previously left reset() with a blank canvas when already at root — see reset()).
function freshInitialState(): GState {
  return {
    selected: null,
    viewBy: null,
    trail: [],
    focus: { expId: null, projId: null },
    history: [{ expId: null, projId: null }],
    histIdx: 0,
  };
}

export function GraphPanel() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const posRef = useRef(new Map<string, PosRec>());
  const viewRef = useRef({ k: 1, x: 0, y: 0 });
  const dragRef = useRef<{
    id: string | null;
    node: GNode | null;
    sx: number;
    sy: number;
    moved: boolean;
    slop: number;
    panX: number;
    panY: number;
  } | null>(null);
  const hoverRef = useRef<string | null>(null);
  const focusRef = useRef<{ id: string; until: number; k?: number } | null>(null);
  const energizeRef = useRef(0);
  const [size, setSize] = useState({ w: 800, h: 560 });
  const [state, setState] = useState<GState>(freshInitialState);

  // Mark the "graph" feature tried whenever the graph actually renders — the carousel mounts only the
  // active slide, so this fires however the graph is reached (tab, default/persisted view, or a link),
  // not just on an explicit tab click.
  useEffect(() => {
    void markFeatureTried("graph");
  }, []);

  const graph = useMemo(() => buildGraph(state), [state]);
  const sc = nodeScale(graph.nodes.length);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((ents) => {
      const r = ents[0].contentRect;
      setSize({ w: Math.max(320, r.width), h: Math.max(420, r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ensure positions
  useEffect(() => {
    const pos = posRef.current;
    const cx = size.w / 2;
    const cy = size.h / 2;
    const f = state.focus;
    const centerId = state.viewBy
      ? tagNodeId(state.viewBy.type, state.viewBy.id)
      : f.projId
        ? "proj:" + f.projId
        : f.expId
          ? "exp:" + f.expId
          : null;
    const children = centerId ? graph.nodes.filter((n) => n.id !== centerId) : [];
    const ringR = Math.min(size.w, size.h) * 0.38;
    graph.nodes.forEach((n) => {
      if (pos.has(n.id)) return;
      if (centerId && n.id === centerId) {
        pos.set(n.id, { x: cx, y: cy, vx: 0, vy: 0 });
        return;
      }
      if (centerId) {
        // lay the focused node's children evenly around it to fill the canvas
        const total = children.length || 1;
        const idx = Math.max(
          0,
          children.findIndex((c) => c.id === n.id),
        );
        const a = (idx / total) * Math.PI * 2 - Math.PI / 2;
        pos.set(n.id, {
          x: cx + Math.cos(a) * ringR,
          y: cy + Math.sin(a) * ringR,
          vx: 0,
          vy: 0,
        });
        return;
      }
      // default scatter (all-companies view): near a connected parent if any
      let px: number;
      let py: number;
      const link = graph.links.find((l) => l.target === n.id || l.source === n.id);
      const parentId = link ? (link.target === n.id ? link.source : link.target) : null;
      if (parentId && pos.has(parentId)) {
        px = pos.get(parentId)!.x + (Math.random() - 0.5) * 80;
        py = pos.get(parentId)!.y + (Math.random() - 0.5) * 80;
      } else {
        // spread top-level (company) nodes wider so their labels don't overlap on first paint,
        // but scale to the canvas so they never start off-screen
        const a = Math.random() * Math.PI * 2;
        const baseR = Math.min(size.w, size.h) * 0.32;
        const rad = baseR + Math.random() * baseR * 0.5;
        px = cx + Math.cos(a) * rad;
        py = cy + Math.sin(a) * rad;
      }
      pos.set(n.id, { x: px, y: py, vx: 0, vy: 0 });
    });
    // graph already changes whenever focus/viewBy change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, size.w, size.h]);

  // simulation + draw loop — rAF runs only while this component is mounted
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    canvas.style.width = size.w + "px";
    canvas.style.height = size.h + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    let raf = 0;
    let alpha = 1;
    const f = state.focus;
    const centerId = state.viewBy
      ? tagNodeId(state.viewBy.type, state.viewBy.id)
      : f.projId
        ? "proj:" + f.projId
        : f.expId
          ? "exp:" + f.expId
          : null;
    const focused = !!centerId;
    const scl = nodeScale(graph.nodes.length);
    const theme = () => {
      const cs = getComputedStyle(document.documentElement);
      return {
        ink: cs.getPropertyValue("--ink").trim() || "#1c1a16",
        accent: cs.getPropertyValue("--accent").trim() || "#0d6e69",
        card: cs.getPropertyValue("--card").trim() || "#fff",
        link:
          document.documentElement.getAttribute("data-theme") === "dark"
            ? "rgba(168,162,158,0.22)"
            : "rgba(120,113,108,0.30)",
        muted: cs.getPropertyValue("--muted").trim() || "#888",
      };
    };

    const tick = () => {
      const pos = posRef.current;
      const ns = graph.nodes;
      const ls = graph.links;
      const cx = size.w / 2;
      const cy = size.h / 2;
      // repulsion
      const repCap = focused ? 80 : 46;
      const repK = focused ? 7000 : 4200;
      for (let i = 0; i < ns.length; i++) {
        const a = pos.get(ns[i].id);
        if (!a) continue;
        for (let j = i + 1; j < ns.length; j++) {
          const b = pos.get(ns[j].id);
          if (!b) continue;
          let dx = a.x - b.x;
          let dy = a.y - b.y;
          let d2 = dx * dx + dy * dy;
          if (d2 < 1) {
            d2 = 1;
            dx = Math.random();
            dy = Math.random();
          }
          const d = Math.sqrt(d2);
          const ff = Math.min(repCap, repK / d2) * alpha;
          const fx = (dx / d) * ff;
          const fy = (dy / d) * ff;
          a.vx += fx;
          a.vy += fy;
          b.vx -= fx;
          b.vy -= fy;
        }
      }
      // springs
      const target = focused
        ? Math.max(150, Math.min(300, Math.min(size.w, size.h) * 0.38))
        : 78;
      for (const l of ls) {
        const a = pos.get(l.source);
        const b = pos.get(l.target);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        const ff = (d - target) * 0.045 * alpha;
        const fx = (dx / d) * ff;
        const fy = (dy / d) * ff;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }
      // centering + integrate (damping smooths every step)
      const centerK = focused ? 0.006 : 0.012;
      for (const n of ns) {
        const p = pos.get(n.id);
        if (!p) continue;
        if (dragRef.current && dragRef.current.id === n.id) continue;
        if (focused && n.id === centerId) {
          p.vx += (cx - p.x) * 0.08;
          p.vy += (cy - p.y) * 0.08;
          p.vx *= 0.7;
          p.vy *= 0.7;
          p.x += p.vx;
          p.y += p.vy;
          continue;
        }
        p.vx += (cx - p.x) * centerK * alpha;
        p.vy += (cy - p.y) * centerK * alpha;
        p.vx *= 0.88;
        p.vy *= 0.88;
        p.x += p.vx;
        p.y += p.vy;
      }
      if (energizeRef.current) {
        alpha = 1;
        energizeRef.current = 0;
      }
      // ease the view to center a focused node and zoom in a touch
      const fo = focusRef.current;
      if (fo) {
        const fp = pos.get(fo.id);
        const vv = viewRef.current;
        if (fp) {
          const tk = fo.k || 1.4;
          vv.k += (tk - vv.k) * 0.12;
          const tx = size.w / 2 - fp.x * vv.k;
          const ty = size.h / 2 - fp.y * vv.k;
          vv.x += (tx - vv.x) * 0.12;
          vv.y += (ty - vv.y) * 0.12;
        }
        if (Date.now() > fo.until) focusRef.current = null;
      }
      alpha *= 0.992;
      if (alpha < 0.02) alpha = 0.02;
      draw();
      raf = requestAnimationFrame(tick);
    };

    const draw = () => {
      resetLabelRects();
      const pos = posRef.current;
      const T = theme();
      const v = viewRef.current;
      ctx.save();
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.translate(v.x, v.y);
      ctx.scale(v.k, v.k);
      // links
      ctx.strokeStyle = T.link;
      ctx.lineWidth = 1 / v.k;
      for (const l of graph.links) {
        const a = pos.get(l.source);
        const b = pos.get(l.target);
        if (!a || !b) continue;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      // nodes
      for (const n of graph.nodes) {
        const p = pos.get(n.id);
        if (!p) continue;
        const meta = GTYPE[n.type];
        const r = meta.r * scl;
        const col = nodeColor(n.type, n.entityId, T);
        const isHover = hoverRef.current === n.id;
        const isSel =
          !!state.selected &&
          state.selected.type === n.type &&
          state.selected.id === n.entityId;

        if (n.type === "experience") {
          const e = getExperience(n.entityId);
          const isCurrent = !!e && e.end === "present";
          // current-employer halo
          if (isCurrent) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, r + 7, 0, 7);
            ctx.fillStyle = T.accent;
            ctx.globalAlpha = isHover ? 0.2 : 0.13;
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          // selection ring
          if (isSel) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, r + 5, 0, 7);
            ctx.strokeStyle = isCurrent ? T.accent : col;
            ctx.lineWidth = 2.5 / v.k;
            ctx.stroke();
          }
          // white disc
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, 7);
          ctx.fillStyle = "#fff";
          ctx.fill();
          // logo (clipped) or initials fallback
          const rec = getLogoImg(n.entityId);
          if (rec.ok && rec.img) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(p.x, p.y, r - 2.5, 0, 7);
            ctx.clip();
            const box = (r - 3) * 2 * 0.82;
            const iw = rec.img.naturalWidth || rec.img.width || 1;
            const ih = rec.img.naturalHeight || rec.img.height || 1;
            const s = Math.min(box / iw, box / ih);
            ctx.drawImage(
              rec.img,
              p.x - (iw * s) / 2,
              p.y - (ih * s) / 2,
              iw * s,
              ih * s,
            );
            ctx.restore();
          } else {
            const m = MONO[n.entityId];
            const letters = m ? m.letters : abbrevCo(n.label).slice(0, 2).toUpperCase();
            ctx.fillStyle = m ? m.color : col;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.font = `700 ${r * 0.62}px "Hanken Grotesk", system-ui, sans-serif`;
            ctx.fillText(letters, p.x, p.y + 0.5);
          }
          // border ring (accent for current employer)
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, 7);
          ctx.lineWidth = (isCurrent ? 2.6 : 1.4) / v.k;
          ctx.strokeStyle = isCurrent ? T.accent : T.muted;
          ctx.globalAlpha = isCurrent ? 1 : 0.5;
          ctx.stroke();
          ctx.globalAlpha = 1;
          // company name centered beneath the bubble
          ctx.fillStyle = T.ink;
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.font = `600 ${(8.5 * scl).toFixed(1)}px "Hanken Grotesk", system-ui, sans-serif`;
          labelBelow(ctx, n.label, p.x, p.y + r + 4, Math.max(r * 3, 80), 10 * scl, 2);
          // "Now" tag
          if (isCurrent) {
            ctx.fillStyle = T.accent;
            ctx.textBaseline = "bottom";
            ctx.font = `700 ${(7 * scl).toFixed(1)}px "Hanken Grotesk", system-ui, sans-serif`;
            ctx.fillText("● NOW", p.x, p.y - r - 4);
          }
          continue;
        }

        // selection ring
        if (isSel) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, r + 5, 0, 7);
          ctx.strokeStyle = col;
          ctx.lineWidth = 2.5 / v.k;
          ctx.stroke();
        }

        if (n.type === "project") {
          // light, outline-only glyph — white disc, colored ring + line icon
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, 7);
          ctx.fillStyle = T.card;
          ctx.globalAlpha = isHover ? 1 : 0.96;
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, 7);
          ctx.lineWidth = (isHover ? 2.4 : 1.8) / v.k;
          ctx.strokeStyle = col;
          ctx.stroke();
          const ic = iconImg("layers", col);
          if (ic && ic.ok && ic.img) {
            const box = r * 0.92;
            ctx.drawImage(ic.img, p.x - box / 2, p.y - box / 2, box, box);
          }
        } else if (n.type === "technology") {
          const src = techLogoSrc(n.entityId);
          let drew = false;
          if (src) drew = drawMarkInDisc(ctx, getImg(src), p.x, p.y, r, "#fff");
          if (!drew) {
            const cat = getTechnology(n.entityId)?.category;
            drawMarkInDisc(
              ctx,
              iconImg((cat ? TECH_CAT_ICON[cat] : undefined) || "cpu", col),
              p.x,
              p.y,
              r,
              "#fff",
            );
          }
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, 7);
          ctx.lineWidth = 1.6 / v.k;
          ctx.strokeStyle = col;
          ctx.globalAlpha = 0.7;
          ctx.stroke();
          ctx.globalAlpha = 1;
        } else {
          // industry / skill / role — colored disc + white icon
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, 7);
          ctx.fillStyle = col;
          ctx.globalAlpha = isHover ? 1 : 0.92;
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.lineWidth = 1.5 / v.k;
          ctx.strokeStyle = T.card;
          ctx.stroke();
          const iname =
            n.type === "industry"
              ? INDUSTRY_ICON[n.entityId] || "hospital"
              : n.type === "skill"
                ? "lightbulb"
                : "trophy";
          const ic = iconImg(iname, "#fff");
          if (ic && ic.ok && ic.img) {
            const box = r * 1.05;
            ctx.drawImage(ic.img, p.x - box / 2, p.y - box / 2, box, box);
          }
        }
        // name beneath the bubble
        ctx.fillStyle = T.ink;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.font = `600 ${(8 * scl).toFixed(1)}px "Hanken Grotesk", system-ui, sans-serif`;
        labelBelow(ctx, n.label, p.x, p.y + r + 4, Math.max(60, r * 3.4), 9.5 * scl, 2);
      }
      ctx.restore();
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // graph is rebuilt on any state change, so it covers focus/viewBy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, size, state.selected]);

  // pointer interaction
  const screenToWorld = (sx: number, sy: number) => {
    const v = viewRef.current;
    return { x: (sx - v.x) / v.k, y: (sy - v.y) / v.k };
  };
  const nodeAt = (sx: number, sy: number): GNode | null => {
    const w = screenToWorld(sx, sy);
    const pos = posRef.current;
    for (let i = graph.nodes.length - 1; i >= 0; i--) {
      const n = graph.nodes[i];
      const p = pos.get(n.id);
      if (!p) continue;
      const r = GTYPE[n.type].r * sc;
      if ((w.x - p.x) ** 2 + (w.y - p.y) ** 2 <= (r + 3) ** 2) return n;
    }
    return null;
  };

  const onDown = (e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const n = nodeAt(sx, sy);
    dragRef.current = {
      id: n ? n.id : null,
      node: n,
      sx,
      sy,
      moved: false,
      slop: tapSlop(e.pointerType),
      panX: viewRef.current.x,
      panY: viewRef.current.y,
    };
    // Capture the pointer so a tap/drag that drifts off the canvas — easy to do with a finger near
    // the edge — still delivers its move/up here; otherwise the gesture is lost and never resolves.
    try {
      wrapRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // best-effort: setPointerCapture throws if the pointer is already released — safe to ignore
    }
  };
  const onMove = (e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const d = dragRef.current;
    if (d) {
      const dx = sx - d.sx;
      const dy = sy - d.sy;
      if (Math.abs(dx) + Math.abs(dy) > d.slop) d.moved = true;
      // Stay still inside the tap slop so a jittery finger tap stays a tap (and still drills) instead
      // of nudging the node or panning the canvas.
      if (d.moved) {
        if (d.id) {
          const p = posRef.current.get(d.id);
          const w = screenToWorld(sx, sy);
          if (p) {
            p.x = w.x;
            p.y = w.y;
            p.vx = 0;
            p.vy = 0;
          }
        } else {
          viewRef.current.x = d.panX + dx;
          viewRef.current.y = d.panY + dy;
        }
      }
    } else {
      const n = nodeAt(sx, sy);
      const newHover = n ? n.id : null;
      if (newHover !== hoverRef.current) {
        hoverRef.current = newHover;
        canvas.style.cursor = n ? "pointer" : "grab";
      }
    }
  };
  const onUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (!d.moved && d.node) handleClick(d.node);
  };
  // The OS can cancel a touch gesture mid-stream (a system swipe, an incoming call). Drop the
  // in-flight drag without firing a click so it doesn't leave a node stuck to the finger.
  const onCancel = () => {
    dragRef.current = null;
  };

  // wheel zoom — native listener so preventDefault works (React's onWheel is passive)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const v = viewRef.current;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const nk = Math.max(0.4, Math.min(3, v.k * factor));
      v.x = sx - (sx - v.x) * (nk / v.k);
      v.y = sy - (sy - v.y) * (nk / v.k);
      v.k = nk;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // selection + breadcrumb for a given focus level (used by history navigation)
  const selForFocus = (f: Focus): Selected | null =>
    f.projId
      ? { type: "project", id: f.projId }
      : f.expId
        ? { type: "experience", id: f.expId }
        : null;
  const trailForFocus = (f: Focus): Crumb[] => {
    const out: Crumb[] = [];
    if (f.expId) {
      const e = getExperience(f.expId);
      if (e) out.push({ type: "experience", id: f.expId, label: abbrevCo(e.company) });
    }
    if (f.projId) {
      const p = getProject(f.projId);
      if (p) out.push({ type: "project", id: f.projId, label: p.title });
    }
    return out;
  };

  const applyFocus = (nf: Focus, pushHistory: boolean) => {
    posRef.current.clear();
    viewRef.current = { k: 1, x: 0, y: 0 };
    focusRef.current = null;
    energizeRef.current = 1;
    setState((prev) => {
      let history = prev.history;
      let histIdx = prev.histIdx;
      if (pushHistory) {
        history = prev.history.slice(0, prev.histIdx + 1);
        history.push(nf);
        histIdx = history.length - 1;
      }
      return {
        ...prev,
        focus: nf,
        history,
        histIdx,
        selected: selForFocus(nf),
        trail: trailForFocus(nf),
      };
    });
  };

  // Deep-link: "See in graph" from a detail page lands here with ?gfocus=experience:<id> or
  // project:<id>. Focus that node once on mount (same path a click takes), then leave the graph
  // to the visitor — don't re-apply on later renders.
  const [searchParams, setSearchParams] = useSearchParams();
  const didDeepFocus = useRef(false);
  useEffect(() => {
    if (didDeepFocus.current) return;
    const gf = searchParams.get("gfocus");
    if (!gf) return;
    didDeepFocus.current = true;
    // One-shot: strip the param immediately so a later reload or remount doesn't re-pin focus and
    // trap the visitor where they've since navigated away. cview stays, so the slide doesn't change.
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete("gfocus");
    setSearchParams(cleaned, { replace: true });
    const sep = gf.indexOf(":");
    if (sep < 0) return;
    const kind = gf.slice(0, sep);
    const id = gf.slice(sep + 1);
    if (!id) return;
    if (kind === "experience") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time deep-link focus applied on mount
      if (getExperience(id)) applyFocus({ expId: id, projId: null }, true);
    } else if (kind === "project") {
      const p = getProject(id);
      if (p && getExperience(p.experienceId)) applyFocus({ expId: p.experienceId, projId: id }, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navTo = (idx: number) =>
    setState((prev) => {
      const i = Math.max(0, Math.min(prev.history.length - 1, idx));
      const nf = prev.history[i];
      posRef.current.clear();
      viewRef.current = { k: 1, x: 0, y: 0 };
      focusRef.current = null;
      energizeRef.current = 1;
      return {
        ...prev,
        focus: nf,
        histIdx: i,
        selected: selForFocus(nf),
        trail: trailForFocus(nf),
      };
    });

  const handleClick = (node: GNode) => {
    energizeRef.current = 1;
    if (
      node.type === "industry" ||
      node.type === "technology" ||
      node.type === "skill" ||
      node.type === "role"
    ) {
      // leaf: just select + gently center the view on it (no re-layout)
      focusRef.current = { id: node.id, until: Date.now() + 750, k: 1.5 };
      setState((prev) => ({
        ...prev,
        selected: { type: node.type, id: node.entityId },
        trail: trailFor(node, graph),
      }));
      return;
    }
    // experience / project: drill the focus, hiding peers, re-space + recenter
    const f = state.focus;
    let nf: Focus;
    if (node.type === "experience") {
      if (f.expId === node.entityId && f.projId)
        nf = { expId: node.entityId, projId: null }; // back to its projects
      else if (f.expId === node.entityId && !f.projId)
        nf = { expId: null, projId: null }; // click center → up to all
      else nf = { expId: node.entityId, projId: null }; // focus this company
    } else {
      const p = getProject(node.entityId);
      const eid = p ? p.experienceId : f.expId;
      if (f.projId === node.entityId) nf = { expId: eid, projId: null }; // click center → back up to company
      else nf = { expId: eid, projId: node.entityId }; // focus this project
    }
    applyFocus(nf, true);
  };
  const reset = () => {
    posRef.current.clear();
    viewRef.current = { k: 1, x: 0, y: 0 };
    focusRef.current = null;
    energizeRef.current = 1;
    // A fresh object every time. setState with the shared constant was a same-reference no-op when
    // already at root, so the "ensure positions" effect never re-ran and the positions we just
    // cleared were never re-seeded — the blank-canvas bug.
    setState(freshInitialState());
  };
  const setViewBy = (type: TagType, id: string) => {
    posRef.current.clear();
    focusRef.current = null;
    setState({
      selected: { type, id },
      viewBy: { type, id },
      trail: [],
      focus: { expId: null, projId: null },
      history: [{ expId: null, projId: null }],
      histIdx: 0,
    });
  };

  /* ── Canvas layout controls (pan + zoom the view; NOT drill-down) ───────
     the remote can dock to either side and minimize down to its header */
  const [remote, setRemote] = useState<{ side: "left" | "right"; min: boolean }>(
    () => {
      try {
        const raw = localStorage.getItem("atlas-graph-remote");
        if (raw) {
          const v = JSON.parse(raw) as { side?: string; min?: boolean };
          if ((v.side === "left" || v.side === "right") && typeof v.min === "boolean")
            return { side: v.side, min: v.min };
        }
      } catch {
        // corrupt value — fall through to the default
      }
      return { side: "right", min: true };
    },
  );
  useEffect(() => {
    localStorage.setItem("atlas-graph-remote", JSON.stringify(remote));
  }, [remote]);
  const zoomBy = (factor: number) => {
    const v = viewRef.current;
    const cx = size.w / 2;
    const cy = size.h / 2;
    const nk = Math.max(0.4, Math.min(3, v.k * factor));
    v.x = cx - (cx - v.x) * (nk / v.k);
    v.y = cy - (cy - v.y) * (nk / v.k);
    v.k = nk;
  };
  const panBy = (dx: number, dy: number) => {
    const v = viewRef.current;
    v.x += dx;
    v.y += dy;
  };
  const recenterView = () => {
    const v = viewRef.current;
    v.k = 1;
    v.x = 0;
    v.y = 0;
    energizeRef.current = 1;
  };

  return (
    <div className="graph-embed" data-c="GraphPanel">
      <p className="section-sub" style={{ textAlign: "center" }}>
        <i>Dive right in — sometimes you need to dredge the depths to find the right stuff.</i>
      </p>
      <div className="section-head">
        <div />
        <div className="row" style={{ gap: 8 }}>
          {state.viewBy && (
            <button
              type="button"
              className="chip chip-mono"
              onClick={() => setState((p) => ({ ...p, viewBy: null }))}
            >
              <Eye size={13} /> Clear view-by
            </button>
          )}
          <button type="button" className="btn btn-sm" onClick={reset}>
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      </div>

      <div className="graph-layout">
        <div
          className="graph-canvas card"
          ref={wrapRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          onPointerCancel={onCancel}
          style={{ touchAction: "none" }}
        >
          <canvas ref={canvasRef} />
          <div className="graph-legend">
            {(Object.entries(GTYPE) as [NodeType, { r: number; label: string }][]).map(
              ([k, m]) => (
                <span key={k} className="glegend-item mono">
                  <span
                    className="dot"
                    style={{
                      background: nodeColor(
                        k,
                        k === "industry" ? "healthcare" : "",
                        LEGEND_THEME,
                      ),
                    }}
                  />
                  {m.label}
                </span>
              ),
            )}
          </div>
          <div
            className={
              "graph-controls" +
              (remote.side === "left" ? " is-left" : "") +
              (remote.min ? " is-min" : "")
            }
            role="group"
            aria-label="Canvas layout controls"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="graph-remote-head">
              <button
                type="button"
                className="gc-mini"
                onClick={() =>
                  setRemote((r) => ({ ...r, side: r.side === "right" ? "left" : "right" }))
                }
                title="Dock to the other side"
                aria-label="Dock to the other side"
              >
                {remote.side === "right" ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
              </button>
              <span className="graph-controls-label mono">Remote</span>
              <button
                type="button"
                className="gc-mini"
                onClick={() => setRemote((r) => ({ ...r, min: !r.min }))}
                title={remote.min ? "Expand remote" : "Minimize remote"}
                aria-label={remote.min ? "Expand remote" : "Minimize remote"}
              >
                {remote.min ? <Plus size={13} /> : <Minus size={13} />}
              </button>
            </div>
            <span className="graph-controls-label mono">Levels</span>
            <div className="graph-levelrow">
              <button
                type="button"
                className="gc-btn"
                disabled={state.histIdx <= 0}
                onClick={() => navTo(state.histIdx - 1)}
                title="Back one level"
                aria-label="Back one level"
              >
                <CornerUpLeft size={15} />
              </button>
              <button
                type="button"
                className="gc-btn"
                disabled={state.histIdx >= state.history.length - 1}
                onClick={() => navTo(state.histIdx + 1)}
                title="Forward one level"
                aria-label="Forward one level"
              >
                <ArrowRight size={15} />
              </button>
            </div>
            <span className="graph-controls-label mono">Canvas</span>
            <div className="graph-zoomrow">
              <button
                type="button"
                className="gc-btn"
                onClick={() => zoomBy(1.2)}
                title="Zoom in"
                aria-label="Zoom in"
              >
                <Plus size={15} />
              </button>
              <button
                type="button"
                className="gc-btn"
                onClick={() => zoomBy(1 / 1.2)}
                title="Zoom out"
                aria-label="Zoom out"
              >
                <Minus size={15} />
              </button>
            </div>
            <div className="graph-navpad">
              <button
                type="button"
                className="gc-btn gc-up"
                onClick={() => panBy(0, 70)}
                title="Pan up"
                aria-label="Pan up"
              >
                <ChevronUp size={15} />
              </button>
              <button
                type="button"
                className="gc-btn gc-left"
                onClick={() => panBy(70, 0)}
                title="Pan left"
                aria-label="Pan left"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                className="gc-btn gc-center"
                onClick={recenterView}
                title="Recenter"
                aria-label="Recenter"
              >
                <Dot size={14} />
              </button>
              <button
                type="button"
                className="gc-btn gc-right"
                onClick={() => panBy(-70, 0)}
                title="Pan right"
                aria-label="Pan right"
              >
                <ChevronRight size={15} />
              </button>
              <button
                type="button"
                className="gc-btn gc-down"
                onClick={() => panBy(0, -70)}
                title="Pan down"
                aria-label="Pan down"
              >
                <ChevronDown size={15} />
              </button>
            </div>
          </div>
        </div>
        <SelectionPanel
          node={state.selected}
          trail={state.trail}
          onViewBy={setViewBy}
          onCrumb={(c) => {
            if (c.type === "experience") applyFocus({ expId: c.id, projId: null }, true);
            else if (c.type === "project") {
              const p = getProject(c.id);
              applyFocus({ expId: p ? p.experienceId : null, projId: c.id }, true);
            } else setViewBy(c.type as TagType, c.id);
          }}
          onClear={() => setState((p) => ({ ...p, selected: null, trail: [] }))}
        />
      </div>
    </div>
  );
}

/* ── Selection panel — breadcrumb trail + metric boxes + actions ──────────*/
interface MetricRow {
  v: number;
  l: string;
  items: { label: string; to: string }[];
}

function SelectionPanel({
  node,
  trail,
  onViewBy,
  onCrumb,
  onClear,
}: {
  node: Selected | null;
  trail: Crumb[];
  onViewBy: (type: TagType, id: string) => void;
  onCrumb: (c: Crumb) => void;
  onClear: () => void;
}) {
  const [openMetric, setOpenMetric] = useState<string | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- collapse the open metric when the selected node changes
    setOpenMetric(null);
  }, [node?.type, node?.id]);
  if (!node) {
    return (
      <aside className="graph-panel card card-pad">
        <p className="eyebrow">Selection</p>
        <p className="muted-strong" style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6 }}>
          Click any bubble to drill in. Click an industry, technology, or skill to
          recenter the graph on it.
        </p>
      </aside>
    );
  }
  const t = node.type;
  const id = node.id;
  const techItem = (tid: string) => ({
    label: getTechnology(tid)?.label || tid,
    to: `/by/technology?tech=${tid}`,
  });
  const skillItem = (sid: string) => ({
    label: getSkill(sid)?.label || sid,
    to: `/by/skill?skill=${sid}`,
  });
  const indItem = (iid: string) => ({
    label: getIndustry(iid)?.label || iid,
    to: `/by/industry?industry=${iid}`,
  });
  let title: string;
  let sub: string;
  let route: string;
  let tag = false;
  let metrics: MetricRow[];
  if (t === "experience") {
    const e = getExperience(id);
    if (!e) return null;
    title = e.company;
    sub = e.role;
    route = `/experience/${id}`;
    const projs = getProjectsForExperience(id);
    metrics = [
      {
        v: projs.length,
        l: "Engagements",
        items: projs.map((p) => ({ label: p.title, to: `/project/${p.id}` })),
      },
      { v: e.technologies.length, l: "Technologies", items: e.technologies.map(techItem) },
      { v: e.skills.length, l: "Skills", items: e.skills.map(skillItem) },
      { v: e.industries.length, l: "Industries", items: e.industries.map(indItem) },
    ];
  } else if (t === "project") {
    const p = getProject(id);
    if (!p) return null;
    const e = getExperience(p.experienceId);
    title = p.title;
    sub = e ? "at " + e.company : "";
    route = `/project/${id}`;
    metrics = [
      { v: p.technologies.length, l: "Technologies", items: p.technologies.map(techItem) },
      { v: p.skills.length, l: "Skills", items: p.skills.map(skillItem) },
      { v: p.industries.length, l: "Industries", items: p.industries.map(indItem) },
    ];
  } else {
    tag = true;
    const o =
      t === "industry"
        ? getIndustry(id)
        : t === "technology"
          ? getTechnology(id)
          : t === "skill"
            ? getSkill(id)
            : getRole(id);
    title = o?.label || id;
    sub = GTYPE[t].label;
    route = `/by/${t}?${TAG_PARAM[t]}=${id}`;
    const roleExps = data.experiences.filter((e) => expTagIds(e, t).includes(id));
    const engProjs =
      t === "role" ? [] : data.projects.filter((p) => projTagIds(p, t).includes(id));
    metrics = [
      {
        v: roleExps.length,
        l: "Roles",
        items: roleExps.map((e) => ({ label: e.company, to: `/experience/${e.id}` })),
      },
    ];
    if (t !== "role")
      metrics.push({
        v: engProjs.length,
        l: "Engagements",
        items: engProjs.map((p) => ({ label: p.title, to: `/project/${p.id}` })),
      });
  }
  const col = nodeColor(t, id, LEGEND_THEME);
  const openItems = metrics.find((m) => m.l === openMetric) || null;
  return (
    <aside className="graph-panel card card-pad">
      <div className="between">
        <span className="eyebrow row" style={{ gap: 7 }}>
          <span
            className="dot"
            style={{ width: 9, height: 9, borderRadius: "50%", background: col }}
          />
          {GTYPE[t].label}
        </span>
        <button type="button" className="icon-btn" onClick={onClear}>
          <X size={15} />
        </button>
      </div>
      <h3 className="display" style={{ fontSize: 21, marginTop: 10, lineHeight: 1.15 }}>
        {title}
      </h3>
      {trail && trail.length > 1 ? (
        <nav className="graph-crumbs">
          {trail.map((c, i) => {
            const last = i === trail.length - 1;
            return (
              <Fragment key={i}>
                {i > 0 && <ChevronRight size={12} />}
                {last ? (
                  <span className="graph-crumb is-last">{c.label}</span>
                ) : (
                  <button
                    type="button"
                    className="graph-crumb graph-crumb-btn"
                    onClick={() => onCrumb(c)}
                    title={`Back to ${c.label}`}
                  >
                    {c.label}
                  </button>
                )}
              </Fragment>
            );
          })}
        </nav>
      ) : (
        sub && (
          <div className="muted-strong" style={{ fontSize: 14, marginTop: 4 }}>
            {sub}
          </div>
        )
      )}
      {metrics.length > 0 && (
        <div className="journey-metrics graph-panel-metrics">
          {metrics.map((m) => (
            <button
              type="button"
              className={"journey-metric graph-metric" + (openMetric === m.l ? " is-open" : "")}
              key={m.l}
              onClick={() => setOpenMetric(openMetric === m.l ? null : m.l)}
              title={`Show ${m.l.toLowerCase()}`}
            >
              <div className="display journey-metric-v">{m.v}</div>
              <div className="journey-metric-l">{m.l}</div>
            </button>
          ))}
        </div>
      )}
      {openItems && openItems.items.length > 0 && (
        <div className="graph-metric-items">
          {openItems.items.slice(0, 8).map((it, i) => (
            <Link key={i} to={it.to} className="graph-metric-chip" title={it.label}>
              {it.label}
            </Link>
          ))}
          {openItems.items.length > 8 && (
            <Link to={route} className="graph-metric-chip is-more">
              +{openItems.items.length - 8} more
            </Link>
          )}
        </div>
      )}
      <div className="col" style={{ gap: 8, marginTop: 16 }}>
        <Link
          to={route}
          className="btn btn-sm btn-secondary"
          style={{ justifyContent: "space-between" }}
        >
          View details <ArrowRight size={15} />
        </Link>
        {tag && (
          <button
            type="button"
            className="btn btn-sm btn-primary graph-viewby"
            onClick={() => onViewBy(t as TagType, id)}
          >
            <Eye size={14} />
            <span>
              View by {GTYPE[t].label}
            </span>
          </button>
        )}
      </div>
    </aside>
  );
}
